import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import type { ZoneGroup, ZoneGroupCategory } from "../types";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export async function fetchZoneGroupCategories(): Promise<ZoneGroupCategory[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-group-categories`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroupCategory[]>;
}

export async function createZoneGroupCategory(input: {
  name: string;
  notes?: string;
}): Promise<ZoneGroupCategory> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-group-categories`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: input.name, notes: input.notes ?? "" }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroupCategory>;
}

export async function updateZoneGroupCategoryApi(
  id: string,
  patch: Partial<Pick<ZoneGroupCategory, "name" | "notes" | "sortOrder">>,
): Promise<ZoneGroupCategory> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-group-categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroupCategory>;
}

export async function deleteZoneGroupCategoryApi(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-group-categories/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}

export async function fetchZoneGroups(zoneId?: string): Promise<ZoneGroup[]> {
  const q = zoneId?.trim() ? `?zoneId=${encodeURIComponent(zoneId.trim())}` : "";
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-groups${q}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroup[]>;
}

export async function createZoneGroupApi(input: {
  zoneId: string;
  categoryId: string;
  name: string;
  notes?: string;
}): Promise<ZoneGroup> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      zoneId: input.zoneId,
      categoryId: input.categoryId,
      name: input.name,
      notes: input.notes ?? "",
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroup>;
}

export async function updateZoneGroupApi(
  id: string,
  patch: Partial<Pick<ZoneGroup, "name" | "notes" | "categoryId" | "sortOrder">>,
): Promise<ZoneGroup> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-groups/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ZoneGroup>;
}

export async function deleteZoneGroupApi(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/geo/zone-groups/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}
